import type { BadgeView } from '@contracts';
import { BadgeGraphic } from '@ui/shared/badges/BadgeGraphic';
import { describeCriterion } from '@ui/shared/badges/criterionText';

/**
 * `BadgeListRow` (settings-rewards.md §11.4) — replaces `BadgeGrid`/
 * `BadgeCell` on phone. At ~358px usable width the desktop's 168px auto-fill
 * grid degenerates to one cell per row regardless of the grid CSS, and a
 * badge cell's internal proportions assume grid-cell width, not full-row
 * width. This renders a single-column stacked list instead: badge graphic
 * left (72px, down from the grid cell's full-width graphic), name/criterion/
 * status stacked right — same three-line content as the desktop cell,
 * rearranged from "graphic above text, centered" to "graphic beside text,
 * left-aligned." Sort order and tap-to-detail-sheet behavior unchanged.
 */
export function BadgeListRow({
  badges,
  onSelect,
}: {
  readonly badges: readonly BadgeView[];
  readonly onSelect: (badge: BadgeView) => void;
}) {
  const sorted = [...badges].sort((a, b) => {
    if (a.state.earned !== b.state.earned) return a.state.earned ? -1 : 1;
    if (a.state.earned && b.state.earned) {
      return b.state.earnedOn.localeCompare(a.state.earnedOn);
    }
    if (!a.state.earned && !b.state.earned) {
      const pa = a.state.progress.target > 0 ? a.state.progress.current / a.state.progress.target : 0;
      const pb = b.state.progress.target > 0 ? b.state.progress.current / b.state.progress.target : 0;
      return pb - pa;
    }
    return 0;
  });

  if (sorted.length === 0) {
    return (
      <p style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
        No badges yet.
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {sorted.map((badge) => (
        <BadgeRow key={badge.badgeId} badge={badge} onSelect={() => onSelect(badge)} />
      ))}
    </div>
  );
}

function BadgeRow({
  badge,
  onSelect,
}: {
  readonly badge: BadgeView;
  readonly onSelect: () => void;
}) {
  const criterionText = describeCriterion(badge.criterion);
  const statusText = badge.state.earned
    ? 'Earned'
    : `${badge.state.progress.current} of ${badge.state.progress.target} done`;
  const progressFraction = badge.state.earned
    ? 1
    : badge.state.progress.target > 0
      ? Math.min(1, badge.state.progress.current / badge.state.progress.target)
      : 0;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={`${badge.name}, ${criterionText}, ${badge.state.earned ? 'earned' : `in progress, ${statusText}`}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-4)',
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        textAlign: 'left',
        width: '100%',
      }}
    >
      <BadgeGraphic
        iconId={badge.iconId}
        earned={badge.state.earned}
        category={null}
        progressFraction={progressFraction}
        size={72}
      />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 'var(--type-caption-size)', fontWeight: 500, color: 'var(--text-primary)' }}>
          {badge.name}
        </div>
        <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', marginTop: 'var(--space-1)' }}>
          {criterionText}
        </div>
        <div
          className="num"
          style={{
            fontSize: 'var(--type-caption-size)',
            color: 'var(--text-muted)',
            marginTop: 'var(--space-1)',
          }}
        >
          {statusText}
        </div>
      </div>
    </button>
  );
}
